import { createContext, useContext, useState, type ReactNode } from "react";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, type ChatRow } from "@/lib/api";
import { qk } from "@/lib/queryKeys";
import { useAuth } from "@/contexts/AuthContext";

const INITIAL_CHAT_LIMIT = 20;
const CHAT_PAGE_SIZE = 10;

export interface ChatHistoryContextValue {
  chats: ChatRow[];
  currentChatId: string | null;
  hasMoreChats: boolean;
  showArchived: boolean;
  setShowArchived: (show: boolean) => void;
  setCurrentChatId: (chatId: string | null) => void;
  loadChats: (limit?: number) => Promise<void>;
  loadMoreChats: () => Promise<void>;
  saveChat: (chat: ChatRow) => void;
  renameChat: (chatId: string, title: string) => Promise<void>;
  moveChat: (chatId: string, projectId: string | null) => Promise<void>;
  deleteChat: (chatId: string) => Promise<void>;
  archiveChat: (chatId: string) => Promise<void>;
  unarchiveChat: (chatId: string) => Promise<void>;
}

const ChatHistoryContext = createContext<ChatHistoryContextValue | undefined>(undefined);

export function ChatHistoryProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, authLoading } = useAuth();
  const queryClient = useQueryClient();
  const [currentChatId, setCurrentChatId] = useState<string | null>(null);
  const [loadedLimit, setLoadedLimit] = useState(INITIAL_CHAT_LIMIT);
  const [showArchived, setShowArchived] = useState(false);

  const { data: rows = [] } = useQuery({
    queryKey: qk.chats(loadedLimit, showArchived),
    queryFn: () => api.chat.list(loadedLimit + 1, showArchived),
    enabled: !authLoading && isAuthenticated,
    placeholderData: keepPreviousData,
  });

  const chats = rows.slice(0, loadedLimit);
  const hasMoreChats = rows.length > loadedLimit;

  const setChatsData = (updater: (current: ChatRow[]) => ChatRow[]) => {
    queryClient.setQueriesData<ChatRow[]>({ queryKey: qk.chatsAll }, (old) => (old ? updater(old) : old));
  };

  const loadChats = async (limit = INITIAL_CHAT_LIMIT) => {
    setLoadedLimit(limit);
    await queryClient.invalidateQueries({ queryKey: qk.chatsAll });
  };

  const loadMoreChats = async () => {
    setLoadedLimit((current) => current + CHAT_PAGE_SIZE);
  };

  const saveChat = (chat: ChatRow) => {
    setChatsData((current) => {
      const withoutExisting = current.filter((item) => item.id !== chat.id);
      return [chat, ...withoutExisting];
    });
  };

  const renameChat = async (chatId: string, title: string) => {
    setChatsData((current) => current.map((chat) => (chat.id === chatId ? { ...chat, title } : chat)));

    try {
      await api.chat.rename(chatId, title);
    } catch (error) {
      void queryClient.invalidateQueries({ queryKey: qk.chatsAll });
      throw error;
    }
  };

  const moveChat = async (chatId: string, projectId: string | null) => {
    let previousChat: ChatRow | undefined;
    for (const [, rows] of queryClient.getQueriesData<ChatRow[]>({ queryKey: qk.chatsAll })) {
      previousChat = rows?.find((item) => item.id === chatId);
      if (previousChat) break;
    }

    setChatsData((current) =>
      current.map((item) =>
        item.id === chatId ? { ...item, project_id: projectId, shared_at: null, source_document_id: item.source_document_id ?? null } : item
      )
    );

    try {
      const moved = await api.chat.move(chatId, projectId);
      setChatsData((current) =>
        current.map((item) =>
          item.id === chatId
            ? { ...item, project_id: moved.project_id, shared_at: moved.shared_at, source_document_id: moved.source_document_id }
            : item
        )
      );
      void queryClient.invalidateQueries({ queryKey: qk.chatsAll });
      void queryClient.invalidateQueries({ queryKey: qk.chat(chatId) });
      if (previousChat?.project_id) {
        void queryClient.invalidateQueries({ queryKey: qk.projectChats(previousChat.project_id) });
      }
      if (projectId) {
        void queryClient.invalidateQueries({ queryKey: qk.projectChats(projectId) });
      }
    } catch (error) {
      if (previousChat) {
        const restoredChat = previousChat;
        setChatsData((current) => current.map((item) => (item.id === chatId ? restoredChat : item)));
      }
      void queryClient.invalidateQueries({ queryKey: qk.chatsAll });
      throw error;
    }
  };

  const deleteChat = async (chatId: string) => {
    let previousCurrentChatId: string | null = null;
    setChatsData((current) => current.filter((chat) => chat.id !== chatId));
    setCurrentChatId((current) => {
      previousCurrentChatId = current;
      return current === chatId ? null : current;
    });

    try {
      await api.chat.delete(chatId);
      queryClient.removeQueries({ queryKey: qk.chat(chatId) });
    } catch (error) {
      void queryClient.invalidateQueries({ queryKey: qk.chatsAll });
      setCurrentChatId(previousCurrentChatId);
      throw error;
    }
  };

  const archiveChat = async (chatId: string) => {
    setChatsData((current) => current.filter((chat) => chat.id !== chatId));

    try {
      await api.chat.archive(chatId);
      void queryClient.invalidateQueries({ queryKey: qk.chatsAll });
    } catch (error) {
      void queryClient.invalidateQueries({ queryKey: qk.chatsAll });
      throw error;
    }
  };

  const unarchiveChat = async (chatId: string) => {
    setChatsData((current) => current.filter((chat) => chat.id !== chatId));

    try {
      await api.chat.unarchive(chatId);
      void queryClient.invalidateQueries({ queryKey: qk.chatsAll });
    } catch (error) {
      void queryClient.invalidateQueries({ queryKey: qk.chatsAll });
      throw error;
    }
  };

  const value: ChatHistoryContextValue = {
    chats,
    currentChatId,
    hasMoreChats,
    showArchived,
    setShowArchived,
    setCurrentChatId,
    loadChats,
    loadMoreChats,
    saveChat,
    renameChat,
    moveChat,
    deleteChat,
    archiveChat,
    unarchiveChat,
  };

  return <ChatHistoryContext.Provider value={value}>{children}</ChatHistoryContext.Provider>;
}

export function useChatHistory() {
  const value = useContext(ChatHistoryContext);
  if (!value) {
    throw new Error("useChatHistory must be used within ChatHistoryProvider");
  }

  return value;
}
